import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isManager } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, Database, Building2, FolderOpen, Phone, Mail, Globe, MapPin, GraduationCap, CalendarCheck, FlaskConical } from "lucide-react";
import { formatDate, trainingTone } from "@/lib/format";
import { toast } from "sonner";

const TONE_STYLE = {
  info: "text-sky-600", pending: "text-amber-600", overdue: "text-rose-600", muted: "text-slate-400",
};

function daysInfo(dateStr) {
  if (!dateStr) return { tone: "muted", text: "Tarih girilmedi" };
  const [y, m, d] = dateStr.split("-").map(Number);
  const diff = Math.ceil((new Date(y, (m || 1) - 1, d || 1) - new Date()) / 86400000);
  if (diff < 0) return { tone: "overdue", text: "Süresi geçti" };
  if (diff <= 30) return { tone: "pending", text: `${diff} gün kaldı` };
  if (diff <= 90) return { tone: "info", text: `${diff} gün kaldı` };
  return { tone: "active", text: `${diff} gün kaldı` };
}

function MetricCard({ m }) {
  return (
    <Card className="border-slate-200" data-testid={`metric-${m.key}`}>
      <CardContent className="pt-5">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{m.label}</div>
        <div className={`font-mono-tmgd text-3xl font-bold mt-2 ${TONE_STYLE[m.tone] || "text-slate-900"}`}>{m.value}</div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [missing, setMissing] = useState([]);
  const [reminders, setReminders] = useState([]);

  const load = () => {
    api.get("/dashboard").then(({ data }) => {
      setData(data);
      if (data.role !== "customer") {
        api.get("/missing-overview").then(({ data }) => setMissing(data)).catch(() => {});
        api.get("/reminders/overview").then(({ data }) => setReminders(data)).catch(() => {});
      }
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/seed/sample-data");
      toast.success(data.note || "Örnek veri yüklendi.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSeeding(false);
    }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>;

  // ---------- Müşteri ana ekranı ----------
  if (data.role === "customer") {
    const org = data.org || {};
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{data.firm?.title || "Firmanız"}</h1>
          <p className="text-slate-500 text-sm mt-1">Hoş geldiniz, {data.user_name}</p>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-display text-lg flex items-center gap-2"><GraduationCap className="h-5 w-5 text-sky-600" /> Aktif Personel & Eğitim Durumu</CardTitle>
                <StatusBadge tone="info">{(data.personnel || []).length} kişi</StatusBadge>
              </CardHeader>
              <CardContent>
                {(data.personnel || []).length === 0 ? (
                  <p className="text-sm text-slate-500">Henüz aktif personel kaydı yok.</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {data.personnel.map((p) => (
                      <div key={p.id} className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`customer-personnel-${p.id}`} onClick={() => navigate(`/egitim-tatbikatlar?personnel=${p.id}`)}>
                        <div><span className="font-medium text-slate-800">{p.name} {p.surname}</span><span className="text-slate-500 text-sm ml-2">{p.duty}</span></div>
                        <StatusBadge tone={trainingTone(p.training_status)}>{p.training_status}</StatusBadge>
                      </div>
                    ))}
                  </div>
                )}
                <Button variant="outline" className="mt-4" onClick={() => navigate("/personel-bildirimleri")} data-testid="customer-notify-btn">
                  Personel Değişikliği Bildir
                </Button>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="customer-durations">
              <CardHeader><CardTitle className="font-display text-lg">Belge Süreleri & Ziyaretler</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">TMFB No: <strong>{data.firm?.tmfb?.number || "—"}</strong> {data.firm?.tmfb?.valid_until && `· Geçerlilik ${formatDate(data.firm.tmfb.valid_until)}`}</span>
                  <StatusBadge tone={daysInfo(data.firm?.tmfb?.valid_until).tone}>{daysInfo(data.firm?.tmfb?.valid_until).text}</StatusBadge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Sözleşme: {data.firm?.contract?.start_date ? formatDate(data.firm.contract.start_date) : "—"} → {data.firm?.contract?.end_date ? formatDate(data.firm.contract.end_date) : "—"} ({data.firm?.contract?.status || "—"})</span>
                  <StatusBadge tone={daysInfo(data.firm?.contract?.end_date).tone}>{daysInfo(data.firm?.contract?.end_date).text}</StatusBadge>
                </div>
                <Button variant="outline" onClick={() => navigate("/ziyaret-raporlarim")} data-testid="customer-visits-link"><CalendarCheck className="h-4 w-4 mr-2" /> Ziyaret Raporları</Button>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="customer-activities-substances">
              <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><FlaskConical className="h-5 w-5 text-sky-600" /> Faaliyetler & Tehlikeli Maddeler</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">ADR Faaliyetleri</div>
                  {(data.activity_labels || []).length === 0 ? <p className="text-sm text-slate-400">Faaliyet seçilmedi.</p> : (
                    <div className="flex flex-wrap gap-2">{data.activity_labels.map((a, i) => <StatusBadge key={i} tone="info">{a}</StatusBadge>)}</div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">İşletmenin İştigal Ettiği Tehlikeli Maddeler</div>
                  {(data.un_substances || []).length === 0 ? <p className="text-sm text-slate-400">Bağlı madde yok.</p> : (
                    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                      {data.un_substances.map((s) => (
                        <div key={s.record_id} className="flex items-center gap-2 p-2.5 text-sm" data-testid={`customer-substance-${s.record_id}`}>
                          <span className="font-mono-tmgd font-semibold text-slate-800 whitespace-nowrap shrink-0">UN {s.un_no}</span>
                          {s.packing_group && <StatusBadge tone="info" className="whitespace-nowrap shrink-0">PG {s.packing_group}</StatusBadge>}
                          <span className="text-xs text-slate-500 truncate min-w-0 flex-1" title={s.name}>{s.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><FolderOpen className="h-5 w-5 text-sky-600" /> Belge Klasörleri</CardTitle></CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {(data.folders || []).map((f) => (
                    <div key={f.code} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`folder-card-${f.code}`} onClick={() => navigate(`/belgelerim?folder=${f.code}`)}>
                      <FolderOpen className="h-5 w-5 text-amber-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">{f.name}</div>
                        <div className="text-[11px] text-slate-400">{f.file_count || 0} belge</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          <Card className="border-slate-200 h-fit">
            <CardHeader><CardTitle className="font-display text-lg">Kuruluş İletişim</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="font-semibold text-slate-800">{org.full_title || org.short_name}</div>
              {org.address && <div className="flex items-start gap-2 text-slate-600"><MapPin className="h-4 w-4 mt-0.5 shrink-0" /> {org.address}</div>}
              {org.phone && <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4" /> {org.phone}</div>}
              {org.email && <div className="flex items-center gap-2 text-slate-600"><Mail className="h-4 w-4" /> {org.email}</div>}
              {org.website && <div className="flex items-center gap-2 text-slate-600"><Globe className="h-4 w-4" /> {org.website}</div>}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ---------- Yönetici / Danışman ana ekranı ----------
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Ana Ekran</h1>
          <p className="text-slate-500 text-sm mt-1">Hoş geldiniz, {data.user_name}</p>
        </div>
        {isManager(user.role) && (
          <Button onClick={seed} disabled={seeding} variant="outline" data-testid="load-sample-data-button">
            {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />} Örnek Veri Yükle
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {(data.metrics || []).map((m) => <MetricCard key={m.key} m={m} />)}
      </div>

      <Card className="border-slate-200" data-testid="reminders-overview">
        <CardHeader><CardTitle className="font-display text-lg">Yaklaşan Süreler & Ziyaretler</CardTitle></CardHeader>
        <CardContent>
          {reminders.length === 0 ? <p className="text-sm text-emerald-600">Yaklaşan uyarı yok.</p> : (
            <div className="divide-y divide-slate-100">
              {reminders.map((f) => (
                <div key={f.firm_id} className="py-2.5 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded" onClick={() => navigate(`/firmalar/${f.firm_id}`)} data-testid={`reminder-firm-${f.firm_id}`}>
                  <div className="font-medium text-slate-800">{f.title}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {f.items.map((it, i) => <StatusBadge key={i} tone={it.tone}>{it.label}: {it.text}{it.date && ` (${it.date})`}</StatusBadge>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="missing-overview">
        <CardHeader><CardTitle className="font-display text-lg">Eksik Evrak Özeti</CardTitle></CardHeader>
        <CardContent>
          {missing.length === 0 ? <p className="text-sm text-emerald-600">Eksik evrak yok.</p> : (
            <div className="divide-y divide-slate-100">
              {missing.map((f) => (
                <div key={f.firm_id} className="py-2.5 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded" onClick={() => navigate(`/firmalar/${f.firm_id}`)} data-testid={`missing-firm-${f.firm_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-800">{f.title}</span>
                    <StatusBadge tone="overdue">{f.missing.length} eksik</StatusBadge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {f.missing.slice(0, 4).map((m, i) => (
                      <span key={i} className="mr-2">{m.doc} ({[m.working_missing && "çalışma", m.signed_missing && "imzalı PDF"].filter(Boolean).join("+")})</span>
                    ))}
                    {f.missing.length > 4 && <span>+{f.missing.length - 4} daha</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-display text-lg flex items-center gap-2"><Building2 className="h-5 w-5 text-sky-600" /> Firmalar</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate("/firmalar")} data-testid="view-all-firms">Tümünü Gör</Button>
        </CardHeader>
        <CardContent>
          {(data.firms || []).length === 0 ? (
            <p className="text-sm text-slate-500 py-4">Henüz firma yok. {isManager(user.role) && "'Örnek Veri Yükle' ile başlayabilir veya Firmalar ekranından ekleyebilirsiniz."}</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.firms.map((f) => (
                <div key={f.id} className="flex items-center justify-between py-3 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded" onClick={() => navigate(`/firmalar/${f.id}`)} data-testid={`dashboard-firm-${f.id}`}>
                  <div>
                    <div className="font-medium text-slate-800">{f.title}</div>
                    <div className="text-xs text-slate-500">{f.city} · {f.activities.join(", ") || "Faaliyet seçilmedi"}</div>
                  </div>
                  <StatusBadge tone={f.is_active ? "active" : "passive"}>{f.is_active ? "Aktif" : "Pasif"}</StatusBadge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
